import React, { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext"; // ✅ IMPORTANT

export default function Login() {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;
  const navigate = useNavigate();
  const { cart, setCart } = useCart(); // ✅ We need this for merging

  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API_BASE}/api/users/login`, form);
      const { token, user } = res.data;

      // Save login
      localStorage.setItem("userToken", token);
      localStorage.setItem("userName", user.name);
      localStorage.setItem("userEmail", user.email);

      // Remove admin session
      localStorage.removeItem("adminToken");
      localStorage.removeItem("isAdmin");

      // ------------------------------------------------------------
      // 🔥 MERGE LOCAL CART + SERVER CART
      // ------------------------------------------------------------
      try {
        const localRaw = localStorage.getItem("relaxb_cart");
        const localCart = localRaw ? JSON.parse(localRaw) : [];

        const serverRes = await axios.get(`${API_BASE}/api/users/cart`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const serverCart = serverRes.data.cart || [];

        // --- Merge items by unique key ---
        const map = new Map();
        const add = (item) => {
          const key = item.key;
          if (map.has(key)) {
            map.set(key, {
              ...map.get(key),
              qty: map.get(key).qty + item.qty,
            });
          } else {
            map.set(key, { ...item });
          }
        };

        localCart.forEach(add);
        serverCart.forEach(add);

        const merged = [...map.values()];

        // save to backend
        await axios.post(
          `${API_BASE}/api/users/cart`,
          { cart: merged },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // save to local & context
        localStorage.setItem("relaxb_cart", JSON.stringify(merged));
        setCart(merged);
      } catch (mergeErr) {
        console.error("Cart merge error:", mergeErr);
      }

      // Redirect
      navigate("/my-orders");
    } catch (err) {
      console.error("Login error", err);
      setError(
        err.response?.data?.error ||
          "Invalid email or password. Please try again."
      );
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-sm bg-white/90 p-8 rounded-2xl shadow-md border border-gray-100">
        <h1 className="text-2xl font-semibold text-center mb-2">
          Sign in to RelaxB
        </h1>
        <p className="text-xs text-gray-500 text-center mb-6">
          Access your orders and save your favourite products.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-black focus:border-black"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label className="text-xs text-gray-700">Password</label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-black focus:border-black"
              placeholder="Enter password"
              required
            />
          </div>

          {error && (
            <div className="text-red-600 text-xs bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2.5 text-white rounded-full font-medium transition ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-900"
            }`}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="mt-4 text-xs text-center text-gray-500">
          Don’t have an account?{" "}
          <Link to="/register" className="text-blue-600 hover:underline">
            Create one
          </Link>
        </p>

        <p className="mt-3 text-xs text-center text-gray-500">
          Forgot your password?{" "}
          <Link to="/forgot-password" className="text-blue-600 hover:underline">
            Reset it
          </Link>
        </p>
      </div>
    </div>
  );
}
